import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Volume2, VolumeX, SkipForward, Loader2, AlertTriangle } from 'lucide-react';
import { landingAPI } from '../lib/api';

const BACKEND_URL = window.location.origin;

const LandingMediaPlayer = ({
    videoId,
    videoUrl,
    posterUrl,
    fallbackImageUrl,
    autoplay = true,
    loop = false,
    muted = true,
    skipEnabled = true,
    skipDelay = 5,
    onSkip,
    onVideoEnd
}) => {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const [status, setStatus] = useState('loading'); // loading | ready | playing | error
    const [isMuted, setIsMuted] = useState(muted);
    const [skipCountdown, setSkipCountdown] = useState(skipEnabled ? skipDelay : 0);
    const [canSkip, setCanSkip] = useState(!skipEnabled);

    const resolveUrl = (url) => {
        if (!url) return '';
        return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
    };

    const fullVideoUrl = resolveUrl(videoUrl);
    const fullPosterUrl = resolveUrl(posterUrl);

    // Skip countdown
    useEffect(() => {
        if (!skipEnabled || canSkip || status !== 'playing') return;
        const interval = setInterval(() => {
            setSkipCountdown(prev => {
                if (prev <= 1) {
                    setCanSkip(true);
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [skipEnabled, canSkip, status]);

    const handleLoadedData = useCallback(() => {
        setStatus('ready');
        if (videoId) {
            landingAPI.reportPlaybackEvent(videoId, 'playback_loaded', {}).catch(() => {});
        }
        if (autoplay && videoRef.current) {
            videoRef.current.muted = true;
            videoRef.current.play()
                .then(() => setStatus('playing'))
                .catch(() => setStatus('ready'));
        }
    }, [videoId, autoplay]);

    const handlePlay = useCallback(() => setStatus('playing'), []);
    const handleEnded = useCallback(() => {
        if (!loop) {
            setStatus('ready');
            onVideoEnd?.();
        }
    }, [loop, onVideoEnd]);

    const toggleMute = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    }, [isMuted]);

    const handleTapToPlay = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.muted = true;
            setIsMuted(true);
            videoRef.current.play()
                .then(() => setStatus('playing'))
                .catch(() => {});
        }
    }, []);

    const handleSkip = useCallback(() => {
        if (canSkip) {
            videoRef.current?.pause();
            onSkip?.();
        }
    }, [canSkip, onSkip]);

    // Error fallback
    if (status === 'error') {
        if (fallbackImageUrl) {
            return (
                <div className="relative w-full rounded-xl overflow-hidden" data-testid="video-fallback-image">
                    <img src={fallbackImageUrl} alt="Media" className="w-full max-h-[300px] object-contain" />
                </div>
            );
        }
        return (
            <div className="flex items-center justify-center p-6 rounded-xl bg-white/5" data-testid="video-error">
                <AlertTriangle className="w-5 h-5 text-amber-400 mr-2" />
                <span className="text-slate-400 text-sm">{t('landingMediaPlayer.videoUnavailable')}</span>
            </div>
        );
    }

    return (
        <div className="relative w-full rounded-xl overflow-hidden bg-black/30" data-testid="landing-media-player">
            {/* Loading state - show poster with spinner */}
            {status === 'loading' && (
                <div className="relative">
                    {fullPosterUrl && (
                        <img src={fullPosterUrl} alt="" className="w-full max-h-[300px] object-contain opacity-40" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-white/70" />
                    </div>
                </div>
            )}

            {/* Video element — uses direct URL for mobile-compatible HTTP Range streaming */}
            {fullVideoUrl && (
                <video
                    ref={videoRef}
                    src={fullVideoUrl}
                    poster={fullPosterUrl || undefined}
                    loop={loop}
                    muted={isMuted}
                    playsInline
                    webkit-playsinline=""
                    x5-playsinline=""
                    preload="metadata"
                    crossOrigin="anonymous"
                    onLoadedMetadata={handleLoadedData}
                    onPlay={handlePlay}
                    onEnded={handleEnded}
                    onError={() => {
                        if (videoRef.current?.error?.code === 4) {
                            setStatus('ready');
                        } else {
                            setStatus('error');
                        }
                    }}
                    className={`w-full max-h-[300px] object-contain ${status === 'loading' ? 'absolute inset-0 opacity-0 pointer-events-none' : ''}`}
                    data-testid="video-element"
                />
            )}

            {/* Play button overlay */}
            {status === 'ready' && (
                <button
                    onClick={handleTapToPlay}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 z-20 cursor-pointer"
                    data-testid="tap-to-play-button"
                >
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Play className="w-8 h-8 text-white ml-1" />
                    </div>
                </button>
            )}

            {/* Controls overlay */}
            {status === 'playing' && (
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent z-20">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={toggleMute}
                            className="p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
                            data-testid="mute-toggle-button"
                        >
                            {isMuted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
                        </button>
                        {skipEnabled && (
                            <button
                                onClick={handleSkip}
                                disabled={!canSkip}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                    canSkip
                                        ? 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 cursor-pointer'
                                        : 'bg-white/5 text-white/40 cursor-default'
                                }`}
                                data-testid="skip-ad-button"
                            >
                                <SkipForward className="w-3.5 h-3.5" />
                                {canSkip ? 'Skip' : `Skip in ${skipCountdown}s`}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LandingMediaPlayer;
